<template lang="pug">
.insights-utilization-widget.insights-widget(
  :class="{ 'widget-remote-office': remoteVsOffice }"
)
  .widget-wrapper
    .widget-common.widget-small.widget-full-height
      .widget-title.d-flex.align-items-center.justify-content-between
        .d-flex.align-items-baseline
          | {{ title }}
          i.info-icon.hi.hi-info-filled.ml-5(
            v-if="tooltip"
            :key="remoteVsOffice"
            v-tooltip="tooltipOptions"
            :title="tooltip"
          )

      .widget-body(:class="bodyClasses")
        template(v-if="isReady")
          template(v-if="!isEmptyWidget")
            //- Standard view
            template(v-if="!remoteVsOffice")
              .widget-main
                .widget-chart
                  InsightsUtilizationWidgetSingleDonut(
                    :value="standardStats.percentage"
                    :thickness="20"
                    :size="132"
                    :tooltip="standardTooltip"
                  )

                  InsightsUtilizationWidgetTargetPill(
                    :period="targetPeriod"
                    :value="targetFormatted"
                    :tooltip="targetTooltip"
                    :can-edit="canEditTarget"
                    :edit-href="editTarget"
                  )

                .widget-panels
                  InsightsUtilizationWidgetStatsGroup(
                    :value="standardStats.avg"
                    label="Daily working avg"
                  )

                  .widget-target
                    InsightsUtilizationWidgetStatusPill(
                      :variant="unifiedDeviationVariant"
                      :icon="unifiedDeviation.deviationIcon"
                      :icon-rotation="unifiedDeviation.deviationIconRotation"
                      :text="unifiedDeviation.deviationText"
                      :value="unifiedDeviation.deviationValue"
                    )

            //- Remote vs Office view
            template(v-else)
              .widget-main
                .widget-stats-panel
                  InsightsUtilizationWidgetStatsGroup(
                    :indicator="remoteStats.label"
                    indicator-variant="remote"
                    :value="remoteStats.avg"
                    label="Daily working avg"
                  )
                  InsightsUtilizationWidgetStatsGroup(
                    :indicator="officeStats.label"
                    indicator-variant="office"
                    :value="officeStats.avg"
                    label="Daily working avg"
                  )

                .widget-chart
                  InsightsUtilizationWidgetDoubleDonut(
                    :remote-value="remoteStats.percentage"
                    :office-value="officeStats.percentage"
                    :remote-tooltip="remoteTooltip"
                    :office-tooltip="officeTooltip"
                    :thickness="15"
                    :size="132"
                  )

                .widget-target
                  InsightsUtilizationWidgetTargetPill(
                    :period="targetPeriod"
                    :value="targetFormatted"
                    :tooltip="targetTooltip"
                    :can-edit="canEditTarget"
                    :edit-href="editTarget"
                  )
                  InsightsUtilizationWidgetStatusPill(
                    :variant="unifiedDeviationVariant"
                    :icon="unifiedDeviation.deviationIcon"
                    :icon-rotation="unifiedDeviation.deviationIconRotation"
                    :text="unifiedDeviation.deviationText"
                    :value="unifiedDeviation.deviationValue"
                  )
          InsightsNoData(
            v-else
            :type="emptyReason"
          )

      .widget-footer-bordered(v-if="showWidgetBenchmarks")
        InsightsBenchmarksAverages(
          :averages="benchmarksAverages"
          :pins="benchmarkPins"
          :is-loading="!isReady"
          :max="200"
          :widget-name="title"
        )
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { formatDuration, secondsToHours } from '~/utils/time_utils';
import { Insights, InsightsTracking } from '~/mixins';
import { mapGetters } from 'vuex';

import InsightsUtilizationWidgetSingleDonut from './InsightsUtilizationWidgetSingleDonut.vue';
import InsightsUtilizationWidgetDoubleDonut from './InsightsUtilizationWidgetDoubleDonut.vue';
import InsightsUtilizationWidgetTargetPill from './InsightsUtilizationWidgetTargetPill.vue';
import InsightsUtilizationWidgetStatusPill from './InsightsUtilizationWidgetStatusPill.vue';
import InsightsUtilizationWidgetStatsGroup from './InsightsUtilizationWidgetStatsGroup.vue';
import { PopoverAvatarUserCardResource } from '~/resources';

const MAX_PERCENTAGE = 200;

export default {
  name: 'InsightsUtilizationWidget',
  components: {
    InsightsUtilizationWidgetSingleDonut,
    InsightsUtilizationWidgetDoubleDonut,
    InsightsUtilizationWidgetTargetPill,
    InsightsUtilizationWidgetStatusPill,
    InsightsUtilizationWidgetStatsGroup,
    InsightsBenchmarksAverages: () => import('~/components/insights/InsightsBenchmarksAverages.vue'),
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
  },
  mixins: [Insights, InsightsTracking],
  data: () => ({
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
  }),
  computed: {
    ...mapGetters({
      widgetData: 'utilization',
    }),
    ...mapGetters([
      'filters',
      'showWidgetBenchmarks',
      'isDayTimePeriod',
      'isTodayTimePeriod',
      'isUserDashboard',
      'canSeeTeam',
      'organizationId',
      'remoteVsOffice',
    ]),
    title() {
      return 'Utilization';
    },
    tooltip() {
      return this.remoteVsOffice
        ? INSIGHTS.tooltips.utilization.remoteVsOfficeContent
        : INSIGHTS.tooltips.utilization.content;
    },
    widgetKey() {
      return INSIGHTS.api.utilization;
    },
    benchmarks() {
      return this.widgetData?.benchmarks;
    },
    benchmarksAverages() {
      if (this.isEmptyWidget) {
        return {};
      }

      if (this.remoteVsOffice) {
        const all = this.benchmarks || {};
        const result = {};

        if (this.widgetData?.worked_remote > 0) {
          result.remote = all.remote;
        }
        if (this.widgetData?.worked_on_site > 0) {
          result.on_site = all.on_site;
        }

        return result;
      }

      return this.benchmarks || {};
    },
    target() {
      return this.widgetData?.target ?? 0;
    },
    targetFormatted() {
      return formatDuration(this.target, { showSeconds: 'never' });
    },
    standardStats() {
      return this.buildStats('', this.widgetData?.worked);
    },
    remoteStats() {
      return this.buildStats('Remote', this.widgetData?.worked_remote);
    },
    officeStats() {
      return this.buildStats('Office', this.widgetData?.worked_on_site);
    },
    standardTooltip() {
      const stats = this.standardStats;
      const avg = this.formatToHm(stats.worked);
      const target = this.formatToHm(this.target);

      return `Utilization: ${avg}<br />${Math.round(stats.realPercentage)}% based on ${target} target`;
    },
    remoteTooltip() {
      const stats = this.remoteStats;
      const avg = this.formatToHm(stats.worked);
      const target = this.formatToHm(this.target);

      return `Remote utilization: ${avg}<br />${Math.round(stats.realPercentage)}% based on ${target} target`;
    },
    officeTooltip() {
      const stats = this.officeStats;
      const avg = this.formatToHm(stats.worked);
      const target = this.formatToHm(this.target);

      return `Office utilization: ${avg}<br />${Math.round(stats.realPercentage)}% based on ${target} target`;
    },
    benchmarkScores() {
      if (this.isEmptyWidget) {
        return {};
      }

      const target = this.widgetData?.target;

      if (this.remoteVsOffice) {
        const scores = {};

        if (this.widgetData?.worked_remote > 0) {
          scores.remote = target > 0 ? Math.round((this.widgetData?.worked_remote * 100) / target) : 0;
        }
        if (this.widgetData?.worked_on_site > 0) {
          scores.on_site = target > 0 ? Math.round((this.widgetData?.worked_on_site * 100) / target) : 0;
        }

        return scores;
      }

      return target > 0 ? Math.round((this.widgetData?.worked * 100) / target) : 0;
    },
    benchmarkPins() {
      const scores = this.benchmarkScores;

      if (typeof scores === 'object') {
        return Object.entries(scores).map(([key, value]) => ({ key, value }));
      }

      return [{ key: 'standard', value: scores }];
    },
    targetTooltip() {
      if (this.isTodayTimePeriod) {
        return `${this.targetPeriod} ${INSIGHTS.tooltips.utilization.target.today}`;
      }

      return `${this.targetPeriod} ${INSIGHTS.tooltips.utilization.target.default}`;
    },
    canEditTarget() {
      return this.canSeeTeam && this.isTodayTimePeriod && this.isUserDashboard;
    },
    targetPeriod() {
      if (this.isDayTimePeriod) {
        const date = this.isTodayTimePeriod ? 'today' : 'yesterday';
        const day = `${INSIGHTS.utilization.dayPeriod[date]}'s`;

        return this.isUserDashboard ? day : `${day} avg.`;
      }

      return this.isUserDashboard ? 'Daily' : 'Avg. daily';
    },
    editTarget() {
      return PopoverAvatarUserCardResource.memberProfileLink({
        organizationId: this.organizationId,
        userId: this.filters.user_id,
        tab: 'work hours',
      });
    },
    unifiedDeviation() {
      const totalWorked = this.widgetData?.worked ?? 0;
      const deviation = this.target - totalWorked;
      const isBelow = deviation > 0;
      const deviationPercent = this.target > 0 ? (deviation / this.target) * 100 : 0;

      const deviationColor = this.getDeviationColor(deviationPercent);
      const deviationSign = isBelow ? '-' : '+';
      const deviationIcon = deviationColor === 'green' ? 'hi-check' : 'hi-arrow-down-narrow';
      const deviationIconRotation = !isBelow && deviationColor !== 'green' ? 'rotate-180' : '';
      const deviationText = deviationColor === 'green' ? 'On target' : isBelow ? 'Below target' : 'Above target';

      return {
        deviationClass: deviationColor,
        deviationIcon,
        deviationIconRotation,
        deviationText,
        deviationValue: `${deviationSign} ${formatDuration(Math.abs(deviation), { showSeconds: 'never' })}`,
      };
    },
    unifiedDeviationVariant() {
      const map = { red: 'bad', orange: 'warn', green: 'ok' };

      return map[this.unifiedDeviation.deviationClass] || 'bad';
    },
  },
  methods: {
    formatToHm(seconds) {
      const [h, m] = secondsToHours(seconds).split(':');

      return `${h}h ${m}m`;
    },
    // Table of colors:
    // 0–50% => red
    // 50–95% => orange
    // 95–105% => green
    // 105–119% => orange
    // 119–200% => red
    getDeviationColor(deviationPercent) {
      const sections = INSIGHTS.utilization.sections;

      if (!sections?.length) {
        return 'red';
      }

      const total = sections.reduce((sum, value) => sum + value, 0);
      const colors = ['red', 'orange', 'green', 'orange', 'red'];
      const workedPercent = Math.max(0, Math.min(MAX_PERCENTAGE, 100 - (deviationPercent ?? 0)));

      let cumulative = 0;

      for (let index = 0; index < sections.length; index++) {
        const min = (cumulative / total) * MAX_PERCENTAGE;

        cumulative += sections[index];
        const max = (cumulative / total) * MAX_PERCENTAGE;
        const isLast = index === sections.length - 1;

        if (workedPercent >= min && (isLast ? workedPercent <= max : workedPercent < max)) {
          return colors[index] || colors[colors.length - 1];
        }
      }

      return 'red';
    },
    buildStats(label, worked) {
      const hasData = worked != null && worked > 0;
      const workedSeconds = worked ?? 0;
      const realPercentage = this.target > 0 ? (workedSeconds / this.target) * 100 : 0;
      const percentage = Math.min(realPercentage, MAX_PERCENTAGE);

      return {
        label,
        worked: workedSeconds,
        avg: hasData ? formatDuration(workedSeconds, { showSeconds: 'never' }) : '--',
        percentage: hasData ? percentage : 0,
        realPercentage,
      };
    },
  },
};
</script>
